/**
 * 前台模块注册表 —— 唯一的扩展点。
 *
 * 新增一个功能模块只需要两步：
 *   1. 在 `src/modules/<key>/` 下建立页面与 `index.ts`
 *   2. 把该模块对象追加到本文件底部的 `modules` 数组
 *
 * `PortalLayout` 只遍历本数组生成导航，不 import 任何具体模块的页面；
 * `router.ts` 由本数组合成路由树。因此新增模块不会触碰 shell 代码。
 *
 * 显式注册而非 `import.meta.glob` 自动发现：
 *   - 构建结果确定，不依赖文件系统遍历顺序
 *   - 模块元数据有类型约束，漏字段在编译期暴露
 *   - 懒加载边界清晰（`routes` 里用动态 import，注册表本身只存元数据）
 */

import type { Component } from 'vue'
import type { RouteRecordRaw } from 'vue-router'

/** 导航条目。`to` 为绝对路径，`exact` 决定激活态是否要求完全匹配。 */
export interface PortalNavItem {
  label: string
  to: string
  /** 可选的页内分区，用于顶部导航；路径仍属于当前模块。 */
  hash?: string
  exact?: boolean
}

export interface PortalModule {
  /** 唯一标识；同时用作权限/埋点前缀 */
  key: string
  /** 模块标题 */
  title: string
  /** 移动端导航图标（@lucide/vue 组件） */
  icon: Component
  /** 模块根路径，须以 `/` 开头且不含尾斜杠 */
  basePath: string
  /** 相对 `basePath` 的子路由；空字符串表示模块首页 */
  routes: RouteRecordRaw[]
  /** 导航条目；为空数组表示该模块不出现在导航中 */
  nav: PortalNavItem[]
  /** 整个模块是否要求登录 */
  requiresAuth?: boolean
  /** 导航排序，升序 */
  order: number
  /** 根路径重定向目标（模块默认落地页）；缺省时取 `nav[0].to` */
  defaultPath?: string
}

import { overviewModule } from '@/modules/overview'
import { catalogModule } from '@/modules/catalog'
import { meModule } from '@/modules/me'
import { coursesModule } from '@/modules/courses'

/** 当前已启用的模块。新增模块只改这里。 */
export const modules: PortalModule[] = [overviewModule, coursesModule, catalogModule, meModule]

/** 按 `order` 排序后的模块列表；布局与路由合成共用同一顺序。 */
export function sortedModules(): PortalModule[] {
  return [...modules].sort((a, b) => a.order - b.order)
}

/**
 * 前台默认落地页。
 *
 * 没有可用模块时返回 null —— 由路由守卫导向占位页，而不是把用户送到空白路由。
 */
export function defaultLandingPath(): string | null {
  const first = sortedModules()[0]
  if (!first) return null
  return first.defaultPath ?? first.nav[0]?.to ?? first.basePath
}
